import React from "react";
import { Input, Button, Form, notification } from "antd";
import { LockOutlined } from "@ant-design/icons";
import HTTP from "../../services/httpService";
import { useNavigate } from "react-router-dom";
import { encrypt } from "../../services/util";

// Reuse the same CSS as the login page — no separate CSS file needed
import "../LogIn/LogIn.css";

const passwordRules = [
    { required: true, message: "Please enter your password." },
    {
        pattern:
            /^(?=.*\d)(?=.*[~!@#$%^&*()\-_=+<>,.?/;:"'{}|\\[\]])(?!.*\s)(?=.*[A-Z])(?=.*[a-z]).{8,16}$/,
        message:
            "8–16 chars with uppercase, lowercase, number & special character.",
    },
];

function ChangePassword() {
    const navigate = useNavigate();
    const staff_id = localStorage.getItem("x-dashboard-staffid");

    async function handleSubmit(values) {
        values.old_password = encrypt(values.old_password);
        values.new_password = encrypt(values.new_password);
        values.confirm_password = encrypt(values.confirm_password);

        const { message, success } = await HTTP("/users/changePassword", {
            ...values,
            staff_id,
        });

        if (!success) {
            notification.error({ message });
        } else {
            notification.success({ message });
            navigate("/");
        }
    }

    return (
        <div className="login-page">
            {/* Ambient glows */}
            <div className="login-glow-1" />
            <div className="login-glow-2" />

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minHeight: "100vh",
                    position: "relative",
                    zIndex: 1,
                    padding: "40px 20px",
                }}
            >
                <div
                    style={{
                        width: "100%",
                        maxWidth: 440,
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 16,
                        padding: "40px 40px 36px",
                        boxShadow:
                            "0 24px 64px rgba(15,23,42,0.12), 0 1px 4px rgba(15,23,42,0.06)",
                    }}
                >
                    {/* Header */}
                    <div style={{ marginBottom: 32 }}>
                        {/* Lock icon */}
                        <div
                            style={{
                                width: 52,
                                height: 52,
                                borderRadius: 14,
                                background: "#f5f3ff",
                                border: "1px solid #ddd6fe",
                                color: "#7C3AED",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 22,
                                marginBottom: 20,
                            }}
                        >
                            <LockOutlined />
                        </div>

                        <h2
                            style={{
                                fontFamily: "'Syne', sans-serif",
                                fontSize: 24,
                                fontWeight: 700,
                                color: "#0f172a",
                                margin: "0 0 6px",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Change Password
                        </h2>
                        <p
                            style={{
                                fontSize: 13,
                                color: "#64748b",
                                margin: 0,
                            }}
                        >
                            Set a new secure password for your account.
                        </p>
                    </div>

                    {/* Password requirements hint */}
                    <div
                        style={{
                            background: "#f5f3ff",
                            border: "1px solid #ddd6fe",
                            borderRadius: 8,
                            padding: "10px 14px",
                            marginBottom: 24,
                        }}
                    >
                        <p
                            style={{
                                fontSize: 11,
                                color: "#6D28D9",
                                margin: 0,
                                fontWeight: 600,
                                marginBottom: 4,
                            }}
                        >
                            Password requirements
                        </p>
                        {[
                            "8–16 characters long",
                            "At least one uppercase & lowercase letter",
                            "At least one number",
                            "At least one special character",
                        ].map((req) => (
                            <div
                                key={req}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginTop: 3,
                                }}
                            >
                                <div
                                    style={{
                                        width: 4,
                                        height: 4,
                                        borderRadius: "50%",
                                        background: "#7C3AED",
                                        flexShrink: 0,
                                    }}
                                />
                                <span
                                    style={{ fontSize: 11, color: "#475569" }}
                                >
                                    {req}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Form */}
                    <div
                        className="login-right change-password-form"
                        style={{
                            width: "auto",
                            padding: 0,
                            background: "transparent",
                            backdropFilter: "none",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Form layout="vertical" onFinish={handleSubmit}>
                            <Form.Item
                                name="old_password"
                                label={
                                    <span
                                        style={{
                                            color: "#475569",
                                            fontSize: 12,
                                            fontWeight: 500,
                                        }}
                                    >
                                        Current Password
                                    </span>
                                }
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            "Please enter your current password.",
                                    },
                                ]}
                            >
                                <Input.Password
                                    autoFocus
                                    placeholder="Enter current password"
                                />
                            </Form.Item>

                            <Form.Item
                                name="new_password"
                                label={
                                    <span
                                        style={{
                                            color: "#475569",
                                            fontSize: 12,
                                            fontWeight: 500,
                                        }}
                                    >
                                        New Password
                                    </span>
                                }
                                rules={passwordRules}
                            >
                                <Input.Password
                                    placeholder="Enter new password"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirm_password"
                                label={
                                    <span
                                        style={{
                                            color: "#475569",
                                            fontSize: 12,
                                            fontWeight: 500,
                                        }}
                                    >
                                        Confirm New Password
                                    </span>
                                }
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            "Please confirm your password.",
                                    },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (
                                                !value ||
                                                getFieldValue(
                                                    "new_password",
                                                ) === value
                                            ) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(
                                                "Passwords do not match.",
                                            );
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    placeholder="Confirm new password"
                                />
                            </Form.Item>

                            <div
                                style={{
                                    display: "flex",
                                    gap: 12,
                                    marginTop: 8,
                                }}
                            >
                                <Button
                                    onClick={() => navigate(-1)}
                                    style={{
                                        flex: 1,
                                        height: 44,
                                        borderRadius: 10,
                                        background: "#f8fafc",
                                        border: "1px solid #cbd5e1",
                                        color: "#334155",
                                        fontSize: 14,
                                        fontWeight: 500,
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className="login-submit-btn"
                                    style={{ flex: 2 }}
                                >
                                    Update Password
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { ChangePassword };
